import React, { useMemo, useRef } from 'react';
import { Button } from 'new/elements/Button';
import GenAiChat, { GenAiChatProps } from 'js/components/elements/GenAiChat/GenAiChat';
import { Box } from '@mui/material';
import Tooltip from 'js/components/elements/mui/Tooltip';
import { useParams } from 'react-router-dom';
import { ReactComponent as GenAiIcon } from 'new/components/icons/genai.svg';
import styles from './GenAiTransformButton.module.scss';
import DraggableElement, {
  DraggableElementHandleMouseDown,
} from 'new/components/DraggableElement/DraggableElement';
import Portal from 'new/elements/Portal';
import { useGenAITransformActions, useGenAITransformState } from './GenAITransformContext';

type GenAiTransformButtonProps = {
  id: string;
  as?: 'button' | 'recommendation';
  buttonText?: string;
  nexsetId?: number;
  handleMouseDown?: DraggableElementHandleMouseDown;
};

const DEFAULT_CHAT_STYLE: GenAiChatProps['style'] = {
  position: 'absolute',
  right: 15,
  bottom: 15,
  maxWidth: '900px',
  maxHeight: '800px',
};

const genAiIcon = <GenAiIcon className={styles.genAiTransformButtonIcon} />;

export const GenAiTransformButton = (props: GenAiTransformButtonProps) => {
  const { as = 'button', buttonText = 'Generate' } = props;
  const params = useParams<{ id: string }>();
  const chatRef = useRef<HTMLDivElement>(null);
  const openedChatBot = useRef('');
  const genAITransformState = useGenAITransformState();
  const genAITransformActions = useGenAITransformActions();

  const { isGenAiChatOpen, selectedCodeTypeOption, endpoint, disabled } = genAITransformState;
  const { toggleGenAIChat, handleGenAIOnCodeSelected, getCurrentContext } = genAITransformActions;

  // If nexsetId is not provided, the component grab the nexsetId from the params
  const nexsetId = props?.nexsetId ?? params?.id;
  const context = useMemo(() => getCurrentContext(), [getCurrentContext]);

  const handleToggleGenAIChat = () => {
    openedChatBot.current = isGenAiChatOpen ? '' : props.id;
    toggleGenAIChat();
  };

  const theme = (() => {
    switch (as) {
      case 'button':
        return styles.genAiTransformButton;
      case 'recommendation':
        return styles.genAiTransformButtonRecommendation;
      default:
        return styles.genAiTransformButton;
    }
  })();

  const open = isGenAiChatOpen && openedChatBot.current === props.id;
  /**
   * @Note
   * - If you want to disable the GenAITransformButton for Nexset Rules
   * - add a handle to render the button based on the feature flag value
   */

  const buttonElement = (
    <Button
      className={theme}
      startIcon={genAiIcon}
      aria-label={`acting-as-${as}`}
      onClick={handleToggleGenAIChat}
      disabled={disabled || isGenAiChatOpen}
    >
      {buttonText}
    </Button>
  );

  const wrappedButtonElement = disabled ? (
    <Tooltip placement="top" title={'This Nexset Rule is currently not supported by NOVA'}>
      <span>{buttonElement}</span>
    </Tooltip>
  ) : (
    buttonElement
  );

  return (
    <Box>
      <DraggableElement>{wrappedButtonElement}</DraggableElement>

      {open && (
        <Portal>
          <GenAiChat
            ref={chatRef}
            endpoint={endpoint}
            nexsetId={nexsetId?.toString()}
            style={DEFAULT_CHAT_STYLE}
            headerTitle="Generate Transform"
            selectedCodeTypeOption={selectedCodeTypeOption}
            // actions
            context={context}
            onClose={handleToggleGenAIChat}
            onCodeSelected={handleGenAIOnCodeSelected}
            onMouseDown={(e) => props?.handleMouseDown?.(e, chatRef)}
          />
        </Portal>
      )}
    </Box>
  );
};
